import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle } from 'lucide-react';
import { useTeamStore } from '../../store/teamStore';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface TeamDeletionSectionProps {
  teamName: string;
  teamId: string;
}

export const TeamDeletionSection: React.FC<TeamDeletionSectionProps> = ({
  teamName,
  teamId
}) => {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTeamOwner, setIsTeamOwner] = useState(false);
  const { deleteTeam } = useTeamStore();
  const { user } = useAuthStore();
 
  useEffect(() => {
    const checkTeamOwnership = async () => {
      if (!user || !teamId) return;
  
      try {
        const { data: team, error } = await supabase
          .from('teams')
          .select('user_id')
          .eq('id', teamId)
          .single();
  
        if (error) {
          console.error('Error checking team ownership:', error);
          return;
        }
  
        setIsTeamOwner(team?.user_id === user.id);
      } catch (error) {
        console.error('Error in checkTeamOwnership:', error);
      }
    };
  
    checkTeamOwnership();
  }, [user, teamId]);

  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmationText !== teamName) {
      toast.error('Team name does not match. Please type the exact team name to confirm deletion.');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteTeam(teamId);
      toast.success(`Team "${teamName}" has been deleted successfully.`);
      setShowConfirmation(false);
      setConfirmationText('');
      
      // Redirect to dashboard after successful deletion
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setConfirmationText('');
  };

  // Only render if user is a head coach
  if (!isTeamOwner) {
    return null;
  }
  return (
    <div className="space-y-3 border-l-4 border-red-500 pl-4">
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Delete Team</h4>
          <p className="text-gray-600 mb-3">
            Permanently delete this team and all associated data. This action cannot be undone.
          </p>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-700">
                <p className="font-semibold mb-2">This will permanently delete:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All players and their information</li>
                  <li>All practices and attendance records</li>
                  <li>All depth charts and formations</li>
                  <li>All drills and practice plans</li>
                  <li>All team settings and preferences</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {!showConfirmation ? (
          <button
            onClick={handleDeleteClick}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete Team
          </button>
        ) : (
          <div className="space-y-3 p-3 bg-gray-50 rounded-lg border">
            <div>
              <p className="text-gray-900 font-semibold mb-2">
                Are you absolutely sure you want to delete "{teamName}"?
              </p>
              <p className="text-gray-600 text-sm mb-3">
                To confirm deletion, please type the team name exactly as shown above:
              </p>
              
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={`Type "${teamName}" to confirm`}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                disabled={isDeleting}
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={handleConfirmDelete}
                disabled={confirmationText !== teamName || isDeleting}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                  confirmationText === teamName && !isDeleting
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Trash2 className="w-4 h-4" />
                {isDeleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
              
              <button
                onClick={handleCancel}
                disabled={isDeleting}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
  );
}; 